import deepEqual from "deep-equal";
import {
  createContext,
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
} from "react";

import { useNavigate, useSearch } from "@tanstack/react-router";

import { useAuthSession } from "@/api/resources/auth/hooks";
import { useDeviceTags } from "@/api/resources/deviceTags/hooks";
import { getAllDeviceTagChildren } from "@/api/resources/deviceTags/utils";
import { defaultGlobalFilters, defaultGlobalFiltersContext, type FilterOptions } from "@/providers/globalFiltersConfig";
import { makeQueryFilterIn, type QueryFilter } from "@/utils/filter";
import { filterNotEmpty } from "@/utils/format";

type QueryFilters = { [key in keyof FilterOptions]: QueryFilter | undefined };

type QueryFilterAddons = Record<
  | "deviceTagsNegation"
  | "caseTagsNegation"
  | "caseTypesNegation"
  | "maintenanceTagsNegation"
  | "deviceTagsChildrenDevices"
  | "deviceTagsChildrenCases"
  | "deviceTagsNegationCases"
  | "deviceTagsChildrenMaintenances"
  | "deviceTagsNegationMaintenances"
  | "deviceTagsChildrenMaintenanceStrategies"
  | "deviceTagsNegationMaintenanceStrategies"
  | "deviceTagsChildrenAssembledParts"
  | "deviceTagsNegationAssembledParts"
  | "deviceModelsAssembledParts",
  QueryFilter[]
>;

const GlobalFiltersContext = createContext<{
  expanded: boolean;
  optionsApplied: boolean;
  toggle: () => void;
  close: () => void;
  resetOptions: () => void;
  options: FilterOptions;
  setOptions: Dispatch<SetStateAction<FilterOptions>>;
  queryFilters: QueryFilters;
  queryFilterAddons: QueryFilterAddons;
  getDynamicQueryFilter: (optionKey: keyof FilterOptions, filterKey: string) => QueryFilter | undefined;
}>(defaultGlobalFiltersContext);

const GlobalFiltersProvider = ({ children }: PropsWithChildren) => {
  const { data: session } = useAuthSession();
  const navigate = useNavigate();
  const { data: availableDeviceTags } = useDeviceTags();

  const filterOptions = useSearch({
    strict: false,
    select: ({
      deployments,
      users,
      manufacturers,
      deviceModels,
      maintenanceStatus,
      deviceTags,
      caseTags,
      caseTypes,
      maintenanceTags,
    }) => ({
      deployments,
      users,
      manufacturers,
      deviceModels,
      maintenanceStatus,
      deviceTags,
      caseTags,
      caseTypes,
      maintenanceTags,
    }),
  });
  const [globalFilterOptions, setGlobalFilterOptions] = useState<FilterOptions>(() => ({
    deployments: filterOptions.deployments ?? defaultGlobalFilters.deployments,
    users: filterOptions.users ?? defaultGlobalFilters.users,
    manufacturers: filterOptions.manufacturers ?? defaultGlobalFilters.manufacturers,
    deviceModels: filterOptions.deviceModels ?? defaultGlobalFilters.deviceModels,
    maintenanceStatus: filterOptions.maintenanceStatus ?? defaultGlobalFilters.maintenanceStatus,
    deviceTags: filterOptions.deviceTags ?? defaultGlobalFilters.deviceTags,
    caseTags: filterOptions.caseTags ?? defaultGlobalFilters.caseTags,
    caseTypes: filterOptions.caseTypes ?? defaultGlobalFilters.caseTypes,
    maintenanceTags: filterOptions.maintenanceTags ?? defaultGlobalFilters.maintenanceTags,
  }));

  const [expanded, setExpanded] = useState(false);

  const toggle = () => setExpanded((_expanded) => !_expanded);
  const close = () => setExpanded(false);
  const resetOptions = useCallback(() => {
    close();
    setGlobalFilterOptions(defaultGlobalFilters);
  }, []);

  const queryFilters = useMemo<QueryFilters>(
    () => ({
      deployments: makeQueryFilterIn({ items: globalFilterOptions.deployments, filterKey: "deployment_id" }),
      users: makeQueryFilterIn({ items: globalFilterOptions.users, filterKey: "assignee_id" }),
      manufacturers: makeQueryFilterIn({ items: globalFilterOptions.manufacturers, filterKey: "manufacturer_id" }),
      deviceModels: makeQueryFilterIn({ items: globalFilterOptions.deviceModels, filterKey: "model_id" }),
      maintenanceStatus: makeQueryFilterIn({ items: globalFilterOptions.maintenanceStatus, filterKey: "status" }),
      deviceTags: makeQueryFilterIn({ items: globalFilterOptions.deviceTags, filterKey: "device_tags.id" }),
      caseTags: makeQueryFilterIn({ items: globalFilterOptions.caseTags, filterKey: "case_tags.id" }),
      caseTypes: makeQueryFilterIn({ items: globalFilterOptions.caseTypes, filterKey: "type" }),
      maintenanceTags: makeQueryFilterIn({
        items: globalFilterOptions.maintenanceTags,
        filterKey: "maintenance_tags.id",
      }),
    }),
    [globalFilterOptions],
  );

  const getDynamicQueryFilter = useCallback(
    (optionKey: keyof FilterOptions, filterKey: string) => {
      return makeQueryFilterIn({ items: globalFilterOptions[optionKey], filterKey });
    },
    [globalFilterOptions],
  );

  const queryFilterAddons = useMemo<QueryFilterAddons>(
    () => ({
      deviceTagsNegation: globalFilterOptions.deviceTags.length > 0 ? [["device_tags", "not.is", null]] : [],
      caseTagsNegation: globalFilterOptions.caseTags.length > 0 ? [["case_tags", "not.is", null]] : [],
      caseTypesNegation: globalFilterOptions.caseTypes.length > 0 ? [["type", "not.is", null]] : [],
      maintenanceTagsNegation:
        globalFilterOptions.maintenanceTags.length > 0 ? [["maintenance_tags", "not.is", null]] : [],
      deviceTagsChildrenDevices: [
        makeQueryFilterIn({
          items: getAllDeviceTagChildren(globalFilterOptions.deviceTags, availableDeviceTags ?? []),
          filterKey: "device_tags.id",
        }),
      ].filter(filterNotEmpty),
      deviceTagsChildrenCases: [
        makeQueryFilterIn({
          items: getAllDeviceTagChildren(globalFilterOptions.deviceTags, availableDeviceTags ?? []),
          filterKey: "device.device_tags.id",
        }),
      ].filter(filterNotEmpty),
      deviceTagsNegationCases:
        globalFilterOptions.deviceTags.length > 0
          ? [
              ["device", "not.is", null],
              ["device.device_tags", "not.is", null],
            ]
          : [],
      deviceTagsChildrenMaintenances: [
        makeQueryFilterIn({
          items: getAllDeviceTagChildren(globalFilterOptions.deviceTags, availableDeviceTags ?? []),
          filterKey: "maintenances_to_devices.device.device_tags.id",
        }),
      ].filter(filterNotEmpty),
      deviceTagsNegationMaintenances:
        globalFilterOptions.deviceTags.length > 0
          ? [
              ["maintenances_to_devices", "not.is", null],
              ["maintenances_to_devices.device", "not.is", null],
              ["maintenances_to_devices.device.device_tags", "not.is", null],
            ]
          : [],
      deviceTagsChildrenMaintenanceStrategies: [
        makeQueryFilterIn({
          items: getAllDeviceTagChildren(globalFilterOptions.deviceTags, availableDeviceTags ?? []),
          filterKey: "maintenance_strategies_to_devices.device.device_tags.id",
        }),
      ].filter(filterNotEmpty),
      deviceTagsNegationMaintenanceStrategies:
        globalFilterOptions.deviceTags.length > 0
          ? [
              ["maintenance_strategies_to_devices", "not.is", null],
              ["maintenance_strategies_to_devices.device", "not.is", null],
              ["maintenance_strategies_to_devices.device.device_tags", "not.is", null],
            ]
          : [],
      deviceTagsChildrenAssembledParts: [
        makeQueryFilterIn({
          items: getAllDeviceTagChildren(globalFilterOptions.deviceTags, availableDeviceTags ?? []),
          filterKey: "device.device_tags.id",
        }),
      ].filter(filterNotEmpty),
      deviceTagsNegationAssembledParts:
        globalFilterOptions.deviceTags.length > 0
          ? [
              ["device", "not.is", null],
              ["device.device_tags", "not.is", null],
            ]
          : [],
      deviceModelsAssembledParts: [
        makeQueryFilterIn({
          items: globalFilterOptions.deviceModels,
          filterKey: "device.model_id",
        }),
      ].filter(filterNotEmpty),
    }),
    [availableDeviceTags, globalFilterOptions],
  );

  const optionsApplied = Object.keys(globalFilterOptions)
    .map((filterKey) => globalFilterOptions[filterKey as keyof typeof globalFilterOptions].length > 0)
    .some((keys) => keys);

  useLayoutEffect(() => {
    // ! ensure we're only messing with search params, when user is authenticated (see Layout/LayoutDeterminer), otherwise breaks Auth Redirect Search Params
    if (session && !deepEqual(globalFilterOptions, filterOptions)) {
      void navigate({
        // @ts-expect-error don't know whats wrong
        search: (prev) => ({
          ...prev,
          ...globalFilterOptions,
        }),
      });
    }
  }, [globalFilterOptions, filterOptions, navigate, session]);

  const value = useMemo(
    () => ({
      expanded,
      optionsApplied,
      toggle,
      close,
      options: globalFilterOptions,
      setOptions: setGlobalFilterOptions,
      resetOptions,
      queryFilters,
      queryFilterAddons,
      getDynamicQueryFilter,
    }),
    [
      expanded,
      globalFilterOptions,
      optionsApplied,
      queryFilters,
      queryFilterAddons,
      getDynamicQueryFilter,
      resetOptions,
    ],
  );

  return <GlobalFiltersContext value={value}>{children}</GlobalFiltersContext>;
};

export { GlobalFiltersContext, GlobalFiltersProvider };

export default GlobalFiltersProvider;
